import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { AdminApiError, fetchOperationLogs } from "~/lib/admin-api";
import { dateDisplayProps, formatDateTime, shortId } from "~/lib/format";
import {
  buildOperationLogsQuery,
  formatOperationAction,
  formatOperationTargetType,
  operationActionBadgeVariant,
  operationLogActionFilters,
  parseOperationReason,
} from "~/lib/operation-logs";

function shouldRetry(failureCount: number, error: Error) {
  if (
    error instanceof AdminApiError &&
    error.status >= 400 &&
    error.status < 500
  ) {
    return false;
  }
  return failureCount < 2;
}

const permissionErrorMessage =
  "管理者権限がありません。権限が変更された可能性があります — 再ログインしてください。";

function loadErrorMessage(error: Error): string {
  if (
    error instanceof AdminApiError &&
    (error.status === 401 || error.status === 403)
  ) {
    return permissionErrorMessage;
  }
  return error.message;
}

interface OperationLogsProps {
  accessToken: string;
}

export function OperationLogs({ accessToken }: OperationLogsProps) {
  const [actionFilter, setActionFilter] = useState<string>("");

  const query = buildOperationLogsQuery(
    actionFilter ? { action: actionFilter } : {},
  );
  const logsQuery = useQuery({
    queryKey: ["operation-logs", query],
    queryFn: ({ signal }) => fetchOperationLogs(accessToken, query, signal),
    retry: shouldRetry,
  });

  return (
    <section className="space-y-6" aria-label="操作ログ">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="grid gap-2">
          <Label htmlFor="operation-log-action">操作で絞り込み</Label>
          <select
            id="operation-log-action"
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
            value={actionFilter}
            onChange={(event) => setActionFilter(event.target.value)}
          >
            <option value="">すべて</option>
            {operationLogActionFilters.map((action) => (
              <option key={action} value={action}>
                {formatOperationAction(action)}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void logsQuery.refetch()}
          disabled={logsQuery.isFetching}
        >
          {logsQuery.isFetching ? "更新中…" : "最新に更新"}
        </Button>
      </div>

      {logsQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>操作ログを取得できませんでした</AlertTitle>
          <AlertDescription>
            {loadErrorMessage(logsQuery.error)}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardContent>
          {logsQuery.isPending ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              操作ログを読み込んでいます…
            </p>
          ) : null}
          {logsQuery.data ? (
            logsQuery.data.logs.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                該当する操作ログはありません。
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>操作</TableHead>
                    <TableHead>日時</TableHead>
                    <TableHead>操作者</TableHead>
                    <TableHead>対象</TableHead>
                    <TableHead>理由・メモ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logsQuery.data.logs.map((log) => {
                    const reason = parseOperationReason(log.reason);
                    return (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Badge variant={operationActionBadgeVariant(log.action)}>
                            {formatOperationAction(log.action)}
                          </Badge>
                        </TableCell>
                        <TableCell
                          {...dateDisplayProps(log.operated_at, formatDateTime)}
                        />
                        <TableCell title={log.operated_by}>
                          {shortId(log.operated_by)}
                        </TableCell>
                        <TableCell title={log.target_id}>
                          {formatOperationTargetType(log.target_type)}{" "}
                          {shortId(log.target_id)}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {reason.note ? (
                              <p className="text-sm">{reason.note}</p>
                            ) : null}
                            {reason.batchRef ? (
                              <p
                                className="text-xs text-muted-foreground"
                                title={reason.batchRef}
                              >
                                一括操作 {shortId(reason.batchRef)}
                              </p>
                            ) : null}
                            {!reason.note && !reason.batchRef ? "—" : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )
          ) : null}
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">
        最新50件を表示しています。ログは追記のみで、変更・削除はできません。
      </p>
    </section>
  );
}
